import { useState, useRef, useCallback, useEffect } from 'react';

interface UseInfiniteScrollOptions<T> {
  fetchFn: (page: number, pageSize: number) => Promise<{ items: T[]; totalCount: number }>;
  pageSize?: number;
  resetDeps?: any[];
}

interface UseInfiniteScrollResult<T> {
  items: T[];
  totalCount: number;
  isLoading: boolean;
  hasMore: boolean;
  sentinelRef: React.RefCallback<HTMLDivElement>;
  reload: () => void;
}

export function useInfiniteScroll<T>({
  fetchFn,
  pageSize = 20,
  resetDeps = [],
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);

  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const requestCounterRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);

  const hasMore = items.length < totalCount;

  const doFetch = useCallback(async (targetPage: number) => {
    setIsLoading(true);
    const requestId = ++requestCounterRef.current;

    try {
      const result = await fetchFnRef.current(targetPage, pageSize);
      // レース条件防止
      if (requestId !== requestCounterRef.current) return;

      if (targetPage === 0) {
        setItems(result.items);
      } else {
        setItems(prev => [...prev, ...result.items]);
      }
      setTotalCount(result.totalCount);
    } catch (error) {
      if (requestId !== requestCounterRef.current) return;
      console.error('Failed to fetch:', error);
    } finally {
      if (requestId === requestCounterRef.current) {
        setIsLoading(false);
      }
    }
  }, [pageSize]);

  // 初回ロード
  useEffect(() => {
    setPage(0);
    setItems([]);
    setTotalCount(0);
    doFetch(0);
  }, resetDeps); // eslint-disable-line react-hooks/exhaustive-deps

  // ページ変更時の追加読み込み（page > 0のみ）
  useEffect(() => {
    if (page > 0) {
      doFetch(page);
    }
  }, [page, doFetch]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setPage(prev => prev + 1);
    }
  }, [isLoading, hasMore]);

  // IntersectionObserver用のcallback ref
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    sentinelNodeRef.current = node;

    if (node) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        },
        { rootMargin: '200px' }
      );
      observerRef.current.observe(node);
    }
  }, [loadMore]);

  const reload = useCallback(() => {
    setPage(0);
    setItems([]);
    setTotalCount(0);
    requestCounterRef.current++;
    doFetch(0);
  }, [doFetch]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return { items, totalCount, isLoading, hasMore, sentinelRef, reload };
}
